/**
 * Replay session history into useOsAgentsIdStore.
 *
 * Called once when the FE opens an existing /os/agents/:id route AND
 * the user's tenant has the v2 flag on. The loader hits api-getorbitx's
 * `/v2/.../session/:id/history` endpoint, which returns uniform
 * turn-shaped items from `agent_session_chat_history`. Legacy v1
 * sessions are backfilled into the same table on first read with
 * synthetic invocation_ids of the form `v1_<session>_<idx>`; their rows
 * carry only user_input + final_answer (no agent_steps / ui_component /
 * ask_user) and naturally degrade to plain text bubbles via the same
 * code path as native v2 turns.
 *
 * Mirrors the live processV2Frame's event_type → step-row mapping in
 * agent-search-v2.tsx#processV2Frame, minus the streaming-specific bits
 * (synth_delta is dropped — replay doesn't recreate the typewriter,
 * final_answer carries the authoritative content).
 */

import i18next from "i18next"

import URLS from "~/constants/urls"
import useOsAgentsIdStore from "~/store/os.agents_.$id"
import type { SuggestionItem } from "~/types/knowledge-os"

import type { IAgentStepEvent } from "./components/agent-steps-ui-v2"

/**
 * Map a `plan_decision.action` enum value (the internal snake_case
 * codes the BE understands: `approve`, `modify`, `cancel`,
 * `retry_execution`, `answer_question`, `approve_with_feedback`,
 * `confirm`) to a user-facing label. The same enum lands in two
 * places:
 *   - live decision rendering in agent-search-v2's resumeWithDecision
 *   - historical replay of a `kind="decision"` user_input turn
 * Both substitute into `label_user_chose_action[_with_summary]`. Labels
 * are sourced from `os_agents.decision_action_<action>` locale keys so
 * translations live alongside the rest of the os_agents strings (en
 * authoritative, other locales inherit en for missing keys via
 * i18next's fallback chain). Unknown actions fall back to a
 * Title-Cased version of the raw value so a future BE action doesn't
 * render as literal snake_case while we wait for a locale update.
 */
const _KNOWN_DECISION_ACTIONS = new Set([
  "approve",
  "approve_with_feedback",
  "modify",
  "cancel",
  "retry_execution",
  "answer_question",
  "confirm"
])

export function friendlyDecisionActionLabel(
  action: string | undefined
): string {
  if (!action) return ""
  if (_KNOWN_DECISION_ACTIONS.has(action)) {
    return _t(`os_agents.decision_action_${action}`)
  }
  // Unknown action: convert snake_case → "Snake Case" as a graceful
  // fallback that's still readable.
  return _humanizeSnakeCase(action)
}

/**
 * Convert a snake_case / kebab-case identifier to Title Case for display.
 * Used for planner-emitted clarification keys (e.g. `account_scope`,
 * `decision_maker_roles`) so the `user_decision` summary row reads as
 * "Account Scope: …" instead of leaking the raw key — see ENG-1257.
 * Exported so the live decision render in `agent-search-v2.tsx`'s
 * `resumeWithDecision` stays in sync with the historical replay path.
 */
export function humanizeClarificationKey(
  key: string,
  clarifications?: Array<{ key?: string; question?: string }> | null
): string {
  // Prefer the actual question text from the clarifications payload
  // when available. Falls back to snake_case humanization for callers
  // that don't have the clarifications array handy (or for keys that
  // don't appear in it — e.g. arbitrary user_decision payloads).
  if (Array.isArray(clarifications) && clarifications.length) {
    const match = clarifications.find((c) => c && c.key === key)
    const q = match?.question
    if (q && q.trim()) {
      // Strip trailing punctuation that doesn't read well in the
      // inline summary ("Partner Name:" → "Partner Name").
      return q.replace(/[\s:?]+$/, "").trim()
    }
  }
  return key
    .split(/[_-]/)
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ")
}

const _humanizeSnakeCase = humanizeClarificationKey

/**
 * Find an existing message bubble in the store whose `invocationId`
 * matches `targetInvocationId`. Used so a navigate-away → navigate-back
 * cycle on an in-progress turn re-attaches into the SAME bubble that
 * the original sendMessage created (whose id is a timestamp, not
 * `${inv}_assistant`), instead of stacking a second bubble next to it.
 *
 * Returns the matched messageId, or `null` if no match.
 */
function _findExistingBubbleForInvocation(
  sessionId: string,
  targetInvocationId: string
): string | null {
  const sessionMessages =
    useOsAgentsIdStore.getState().messages?.[sessionId] || {}
  for (const [id, msg] of Object.entries(sessionMessages)) {
    if ((msg as any)?.invocationId === targetInvocationId) {
      return id
    }
  }
  return null
}

/**
 * Locale resolver. The replay path is a plain function (not a React
 * hook), so we use `i18next.t` directly off the singleton (initialised
 * by entry.client.tsx). Wrapped in a tiny helper so call sites stay
 * concise and so we have one place to change if the i18n machinery is
 * ever swapped.
 */
const _t = (key: string, opts?: any) =>
  i18next.t(key, opts) as unknown as string

interface HistoryItem {
  invocation_id: string | null
  kind: "query" | "resume"
  user_input?:
    | {
        kind: "query"
        text: string
        // Files attached to the original turn. Populated by chatbot-v2.js
        // for new rows. Legacy rows persisted before ENG-1250's fix
        // don't carry this field — handled as optional + empty fallback.
        files?: Array<{ name: string; id?: string }> | null
        // Chip-click label override. Set by Node's chatbot-v2.js when
        // the FE passed `display_text` alongside the full prompt in
        // the POST body. When present, prefer it over `text` for the
        // user-bubble content so a refresh shows the chip label
        // instead of the underlying prompt (ENG-1290 follow-up).
        display_text?: string
      }
    | {
        kind: "decision"
        action: string
        answer?: string | Record<string, string | string[]> | null
      }
    | null
  assistant_steps: any[]
  final_answer: string | null
  ui_component?: { resource_name: string | null; params: any } | null
  ask_user?: {
    source: string | null
    clarifications: any[]
    /**
     * Optional prompt shipped by the BE for in-plan step_confirmation
     * gates — preserves the planner-curated phrasing through replay
     * (otherwise we fall back to a generic "Please respond to
     * continue:"). Older history rows may not have this; treated as
     * optional for backward compat.
     */
    prompt?: string | null
  } | null
  status: string
  user_id?: number | string | null
  user_email?: string | null
  research_enabled?: boolean | null
  enqueued_at?: string | null
  finished_at?: string | null
  suggestions?: SuggestionItem[] | null
}

/**
 * Map a raw v2 SSE event payload to an `IAgentStepEvent` row. Mirrors
 * the live processV2Frame logic; keep in sync if either side adds new
 * event_types.
 *
 * `isLiveAwaiting` distinguishes "this turn is currently paused on
 * ask_user (form will render below)" from "this turn was paused but
 * the user answered in a subsequent turn" (historical — no form). It
 * only affects the ask_user step's title/status (e.g. "Waiting on
 * you" vs "Awaited your input") so the replay reads correctly.
 */
function _eventToStep(
  evt: any,
  ord: number,
  isLiveAwaiting: boolean = false
): IAgentStepEvent | null {
  const t = evt?.event_type as string | undefined
  switch (t) {
    case "progress":
      return {
        id: ord,
        eventType: "progress",
        title:
          evt.message ||
          _t("os_agents.label_progress_stage", {
            stage: evt.stage || _t("os_agents.label_progress_working")
          }),
        status: "done",
        stepId: evt.stage,
        raw: evt
      }
    case "plan_proposed": {
      const steps = (evt.plan?.steps || []) as Array<{
        id: string
        title?: string
      }>
      const stepList = steps
        .map((s, i) => `${i + 1}. ${s.title || s.id}`)
        .join("\n")
      const detail = [evt.rationale, stepList ? `Steps:\n${stepList}` : ""]
        .filter(Boolean)
        .join("\n\n")
      return {
        id: ord,
        eventType: "plan_proposed",
        title: _t("os_agents.label_plan_ready_awaiting_approval", {
          count: steps.length
        }),
        status: "done",
        detail,
        raw: evt
      }
    }
    case "plan_auto_executing":
      // No detail — match live (see agent-search-v2.tsx). The
      // planner's `rationale` can leak refusals / scope explanations
      // that read oddly under an "Auto-executing plan" chip.
      return {
        id: ord,
        eventType: "plan_auto_executing",
        title: _t("os_agents.label_auto_executing_plan"),
        status: "done",
        raw: evt
      }
    case "plan_step_started":
      return {
        id: ord,
        eventType: "plan_step_started",
        title:
          evt.title ||
          _t("os_agents.label_step_started_fallback", {
            stepId: evt.step_id || "?"
          }),
        status: "done",
        stepId: evt.step_id,
        raw: evt
      }
    case "plan_step_completed":
      // Match live mode: the chip carries NO detail. The step's text
      // (often the full plan_step body, ~hundreds-of-chars markdown)
      // already renders as the message body below the timeline; piping
      // it into the chip too duplicates content. Live `processV2Frame`
      // intentionally passes "" to markLastRunningByPrefix on this
      // event — history kept showing slice(0, 280) which caused the
      // "history shows extra data not in live" complaint
      // (RCA 2026-05-15 — agent selection / Refining repro).
      return {
        id: ord,
        eventType: "plan_step_completed",
        title: _t("os_agents.label_step_complete_suffix", {
          title: evt.title || _t("os_agents.label_step")
        }),
        status: "done",
        stepId: evt.step_id,
        raw: evt
      }
    case "plan_step_skipped":
      // Match live mode: NO chip. plan_step_skipped is the cascade-
      // fallback "if needed" path — when the upstream succeeded, the
      // fallback step is silently skipped because it wasn't needed.
      // It's internal plumbing, not a user-relevant event.
      // (Live: `case "plan_step_skipped": return` — also no chip.)
      return null
    case "plan_step_fallback_triggered":
      // Match live mode: friendly "Trying backup lookup (web search)"
      // chip when the primary agent returned empty data and the
      // runtime is trying the web-search backup. Status is `done`
      // here (history: events have all played out) — live keeps it
      // `running` and closes it via the next plan_step_completed /
      // plan_step_failed. No detail either way; raw `primary_reason`
      // / `primary_outcome` are internal plumbing.
      return {
        id: ord,
        eventType: "plan_step_fallback_triggered",
        title: _t("os_agents.label_trying_backup_lookup"),
        status: "done",
        stepId: evt.step_id,
        raw: evt
      }
    case "plan_step_substep": {
      // Cross-account sweep chunk progress — emitted by
      // BatchedAccountSweepAgent per ~200-account batch. Renders as a
      // nested status row under the parent plan step in the timeline.
      // Marked "done" on replay (the live processV2Frame uses
      // "running"; on history these are inherently complete).
      const idx = Number(evt?.substep_idx) || 0
      const total = Number(evt?.substep_total) || 0
      const rowsSoFarRaw = evt?.rows_so_far
      // Hide row count when sentinel -1 is emitted (parallel chunks —
      // cumulative count isn't meaningful). Without this we rendered
      // "-1 rows so far" on every replay row.
      const rowsSoFar =
        typeof rowsSoFarRaw === "number" && rowsSoFarRaw >= 0
          ? rowsSoFarRaw
          : null
      const detailParts: string[] = []
      if (total > 0)
        detailParts.push(_t("os_agents.label_chunk_of_total", { idx, total }))
      if (rowsSoFar !== null)
        detailParts.push(
          _t("os_agents.label_rows_so_far", { count: rowsSoFar })
        )
      // Same fallback semantics as the live processV2Frame: BE
      // supplies a curated title; locale fallback only fires when
      // the BE forgot one (rare).
      const fallbackTitle = total
        ? _t("os_agents.label_sweep_chunk", { idx, total })
        : _t("os_agents.label_sweep_chunk_short", { idx })
      return {
        id: ord,
        eventType: "plan_step_substep",
        title: evt?.title || fallbackTitle,
        status: "done",
        stepId: evt?.step_id,
        detail: detailParts.length ? detailParts.join(" · ") : undefined,
        raw: evt
      }
    }
    case "synth_started":
      return {
        id: ord,
        eventType: "synth_started",
        title: evt.title || _t("os_agents.label_compiling_final_answer"),
        status: "done",
        raw: evt
      }
    case "synth_completed":
      return {
        id: ord,
        eventType: "synth_completed",
        title: evt.title || _t("os_agents.label_final_answer_compiled"),
        status: "done",
        raw: evt
      }
    case "synth_delta":
      // Streaming chunks — never rendered in history. final_answer
      // (carried separately on the turn) is the authoritative output.
      return null
    case "critic_verdict":
      // Critic was removed June 2026. Old sessions may still have
      // critic_verdict frames in their stored event stream; drop them
      // silently on replay so the timeline doesn't show stale chips
      // for a layer that no longer exists.
      return null
    case "sources_collected": {
      // Two flavours of sources_collected exist in history:
      //   (a) Synthetic frame persisted by api-getorbitx tee on stream
      //       end (chatbot-v2.js stepsToPersist). Has the sources arrays
      //       but is meant to rebuild the message.source on refresh —
      //       not a user-facing timeline chip. Detect by ABSENCE of
      //       `sources` array (it carries source_doc_ids /
      //       source_chunk_ids / web_search_results instead).
      //   (b) Live BE event from _LeafFinalizer carrying leaf-step
      //       websearch URLs as `sources` + `count`. Render as a chip
      //       in the timeline so the user can see at-a-glance how many
      //       URLs were collected for the answer.
      const hasLiveSources =
        Array.isArray((evt as any).sources) && (evt as any).sources.length > 0
      if (!hasLiveSources) {
        return null
      }
      const cnt = (evt as any).count ?? ((evt as any).sources || []).length
      return {
        id: ord,
        eventType: "sources_collected",
        title: _t("os_agents.label_sources_collected", {
          count: cnt,
          defaultValue: `Collected ${cnt} sources`
        }),
        status: "done",
        raw: evt
      }
    }
    case "ask_user": {
      const clars = (evt.clarifications || []) as Array<{
        question?: string
        key?: string
      }>
      const detail = clars.length
        ? clars
            .map((c, i) => `${i + 1}. ${c.question || c.key || "?"}`)
            .join("\n")
        : ""
      // Live awaiting → "Waiting on you" (form renders below).
      // Historical (already answered in a later turn) → past tense so
      // the replay reads correctly. Without this we showed "Waiting on
      // you" even when the user had clearly already responded.
      // Only the past-tense variant is locale-keyed for now — the
      // existing "Waiting on you" hardcode predates locale work and
      // is unchanged. Keep both routed through the same string so a
      // future cleanup can drop the literal in one move.
      //
      // Detail strategy: hide the question text while the form is LIVE
      // (the form below already shows it — duplication looks bad and
      // the chip's typography mismatches the form's). Restore detail
      // in the HISTORICAL replay so a past-turn chip preserves the
      // record of what was asked (form is gone by then).
      return {
        id: ord,
        eventType: "ask_user",
        title: isLiveAwaiting
          ? _t("os_agents.label_waiting_on_you")
          : _t("os_agents.label_awaited_your_input"),
        status: "done",
        detail: isLiveAwaiting ? "" : detail,
        raw: evt
      }
    }
    case "planner_error":
    case "executor_error":
      return {
        id: ord,
        eventType: t,
        title:
          t === "planner_error"
            ? _t("os_agents.label_planner_failed")
            : _t("os_agents.label_executor_failed"),
        status: "failed",
        detail: evt.reason || evt.error,
        raw: evt
      }
    case "error":
      // Generic pipeline failure (e.g. "v2 chat-agent enqueue failed").
      // Don't surface as a step chip in history — the assistant message
      // already shows the friendly GENERIC_ERROR_MESSAGE bubble for any
      // turn flagged isFailed. A second tech-jargon chip is noise.
      return null
    default:
      return null
  }
}

// Resolved at call time via i18next (see _t). Defined as a getter to
// avoid capturing the locale at module-import time before i18next is
// fully initialised in entry.client.tsx.
const _genericErrorMessage = () => _t("os_agents.label_generic_error")

/**
 * Push a session's full history into the message store. Idempotent
 * within a single session_id — call once after the loader returns.
 * Subsequent live frames append/update on top of this baseline.
 *
 * Message keys are deterministic per `${invocation_id}_user/_assistant`.
 * Backfilled legacy turns use synthetic ids (`v1_<session>_<idx>`) and
 * render through the same path; their thin payload (empty steps, null
 * ui_component) naturally produces plain text bubbles.
 *
 * Returns a re-attach hint when the LAST turn is `in_progress` — the
 * page reloaded mid-turn and the caller should subscribe to the live
 * SSE stream on `invocation_id` (via sendMessageRef.current.reattach)
 * to pick up where the original POST stream left off. Returns `null`
 * for everything else.
 */
export interface ReattachHint {
  invocationId: string
  assistantMessageId: string
}

export function replayV2HistoryIntoStore(
  sessionId: string,
  history: HistoryItem[],
  setMessages: (sessionId: string, value: any) => void,
  // Optional — when provided, the replay enriches any sources_collected
  // step async via /v2/.../sources/resolve so the Sources drawer
  // populates with full doc rows after refresh. Without it, only the
  // External-sources dropdown renders (web_search_results doesn't
  // need enrichment).
  tokenInfo?: {
    tenant_id?: number | string
    user_id?: number | string
    token?: string
  } | null
): ReattachHint | null {
  if (!Array.isArray(history) || history.length === 0) return null

  // Only the LAST turn can carry a live askUser prompt. Any earlier
  // awaiting_user turn was answered by a subsequent kind="resume" turn;
  // re-rendering its prompt as an interactive form would let the user
  // re-answer something they already answered. The timeline steps
  // (`Waiting on you`, `Plan ready — ... awaiting approval`) tell the
  // historical story without re-prompting.
  const lastIdx = history.length - 1
  history.forEach((turn, turnIdx) => {
    _replayV2Turn(
      sessionId,
      turn,
      turnIdx,
      turnIdx === lastIdx,
      setMessages,
      tokenInfo,
      turnIdx > 0 ? history[turnIdx - 1] : undefined
    )
  })

  // Restore showSplitter from the last SalesPitchDeckBuilderV2 ui_component in history.
  // Allows the right panel to reopen when the user navigates away and returns to the
  // same chat — without this, showSplitter stays null after resetOSAgentsIdStore clears it.
  // Safe: any live ui_component SSE event in agent-search-v2 calls setShowSplitter again
  // and overrides this, so the normal happy flow is unaffected.
  //
  // For mode="generate" cards: chatId in AgentRightPanel = sessionId (OS agents route $id).
  // If the deck was completed, localStorage holds the ppt session under
  // `pptbuilder-v2-session-chat-{sessionId}`. Rewrite params to mode="resume" so the
  // right panel renders the existing deck without triggering a fresh generation.
  // If localStorage is empty (cleared / different device), skip setShowSplitter — the
  // user can re-click Preview which re-invokes the agent tool with its own cache check.
  for (let i = history.length - 1; i >= 0; i--) {
    const turn = history[i]
    const uiEvents = (turn.assistant_steps || []).filter(
      (e: any) =>
        e?.event_type === "ui_component" &&
        e?.resource_name === "SalesPitchDeckBuilderV2"
    )
    const legacyCard =
      turn.ui_component?.resource_name === "SalesPitchDeckBuilderV2"
        ? turn.ui_component
        : null
    if (uiEvents.length > 0 || legacyCard) {
      const lastEv = uiEvents[uiEvents.length - 1]
      const card = lastEv
        ? {
            resourceName: lastEv.resource_name,
            resourceParams: lastEv.params || {}
          }
        : {
            resourceName: legacyCard!.resource_name,
            resourceParams: legacyCard!.params || {}
          }

      let resourceParams = card.resourceParams
      if (resourceParams?.mode === "generate") {
        const savedPptSessionId = localStorage.getItem(
          `pptbuilder-v2-session-chat-${sessionId}`
        )
        if (!savedPptSessionId) {
          // No completed session in localStorage — skip auto-open.
          // User re-clicks Preview → agent re-runs → cache hit → resume.
          break
        }
        // Deck completed — rewrite to resume so the panel shows it without regenerating.
        resourceParams = {
          ...resourceParams,
          mode: "resume",
          ppt_session_id: savedPptSessionId
        }
      }

      useOsAgentsIdStore.getState().setShowSplitter({
        parts: [turn.final_answer || ""],
        resourceName: card.resourceName,
        resourceParams,
        cards: [{ stepId: "", resourceName: card.resourceName, resourceParams }]
      })
      break
    }
  }

  // Re-attach hint: only emitted for the LAST turn AND only when its
  // status is `in_progress`. Earlier in-progress rows would be
  // reconciled by api-getorbitx's history-API lazy reconciler before
  // they reach us — if one slips through, we ignore it (re-attaching
  // to a non-latest turn would write into the wrong message bubble).
  const lastTurn = history[lastIdx]
  if (lastTurn && lastTurn.status === "in_progress" && lastTurn.invocation_id) {
    // Prefer the existing-bubble id over the synthetic
    // `${inv}_assistant` so navigate-away → navigate-back reuses the
    // bubble that the original sendMessage created (whose id is a
    // timestamp). Falls back to the synthetic id on a fresh page load.
    const existingBubbleId = _findExistingBubbleForInvocation(
      sessionId,
      lastTurn.invocation_id
    )
    const assistantMessageId =
      existingBubbleId || `${lastTurn.invocation_id}_assistant`
    // eslint-disable-next-line no-console
    console.info(
      `[v2-replay] reattach hint emitted invocation=${lastTurn.invocation_id} ` +
        `bubble=${existingBubbleId ? "existing" : "fresh"}`
    )
    return {
      invocationId: lastTurn.invocation_id,
      assistantMessageId
    }
  }
  return null
}

function _replayV2Turn(
  sessionId: string,
  turn: HistoryItem,
  turnIdx: number,
  isLast: boolean,
  setMessages: (sessionId: string, value: any) => void,
  tokenInfo?: {
    tenant_id?: number | string
    user_id?: number | string
    token?: string
  } | null,
  // The immediately-preceding turn. For a kind="decision" answer turn this
  // is the awaiting_user turn that posed the questions — we pair its
  // ask_user.clarifications with this turn's answer to rebuild the
  // "Your answers" recap card on reload (no separate persisted event needed).
  prevTurn?: HistoryItem
): void {
  const userInput = turn.user_input
  const invId = turn.invocation_id || `v2_idx_${turnIdx}`

  // 1. USER message — only for fresh queries. Resume turns share the
  // prior turn's user bubble; instead we emit a "user_decision" step
  // at the head of the assistant timeline (below).
  //
  // Restore upload chips from history. chatbot-v2.js (post-ENG-1250)
  // persists the uploaded-file list under user_input.files alongside
  // the original (pre-amendment) text, so reload renders the same chip
  // row as the live turn. Legacy rows written before the fix carry the
  // planner-hint suffix inline in `text` and have no `files` array —
  // strip the trailing `\n\n[Uploaded document(s) in this turn: …]`
  // before display so the user-bubble reads cleanly even on un-migrated
  // history (the file names are unrecoverable for those rows, so chips
  // simply don't render — same as v1 history items without files).
  if (userInput?.kind === "query") {
    const rawText = userInput.text || ""
    const cleanedText = rawText.replace(
      /\n\n\[Uploaded document\(s\) in this turn:[^\]]*\]\s*$/,
      ""
    )
    const files = Array.isArray((userInput as any).files)
      ? (userInput as any).files
      : []
    // Chip-click — prefer the chip label persisted by Node's
    // chatbot-v2.js over the underlying prompt so the user-bubble
    // on a refresh matches what the user originally saw in the
    // live turn. Free-text turns omit display_text → fall through
    // to cleanedText as before.
    const displayText =
      typeof (userInput as any).display_text === "string" &&
      (userInput as any).display_text.trim()
        ? (userInput as any).display_text.trim()
        : null
    setMessages(sessionId, {
      role: "user",
      id: `${invId}_user`,
      content: displayText || cleanedText,
      userEmail: turn.user_email ?? null,
      researchEnabled:
        turn.research_enabled ?? (userInput as any).research_enabled ?? false,
      timestamp: turn.enqueued_at
        ? new Date(turn.enqueued_at).getTime()
        : undefined,
      ...(files.length > 0 ? { files } : {})
    })
  }

  // 2. ASSISTANT message — the agent_steps timeline + final content.
  const stepRows: IAgentStepEvent[] = []

  if (userInput?.kind === "decision") {
    const action = userInput.action || ""
    const friendlyAction = friendlyDecisionActionLabel(action)
    const answer = userInput.answer
    const answerObj =
      answer && typeof answer === "object" && !Array.isArray(answer)
        ? (answer as Record<string, unknown>)
        : null
    const isPmbV2DefaultPick = answerObj?.mode === "default"
    // pmb_q2 was dropped from the picker on 2026-05-19 — match any pmb_*
    // namespaced key so replay handles old AND new picker submits.
    const isPmbV2PersonalizedPick =
      answerObj != null &&
      Object.keys(answerObj).some((k) => k.startsWith("pmb_"))
    const isPmbV2Pick = isPmbV2DefaultPick || isPmbV2PersonalizedPick

    if (!isPmbV2Pick) {
      // Clarification answers (`answer_question`) reload as the structured
      // "Your answers" recap CARD — the same view the user saw live. We
      // rebuild it purely from data already in history: THIS turn's `answer`
      // paired with the PRIOR awaiting turn's `ask_user.clarifications` (for
      // the real question text). A clarification whose key is absent from
      // `answer` was skipped → empty answer → "Skipped" (rendered FE-side).
      // No separately-persisted recap event is needed. Old sessions (which
      // predate the skip feature) simply have every key answered; if the
      // prior clarifications can't be located we fall through to the flat
      // "You chose: Answer → …" summary, so nothing ever breaks.
      const priorClarifs: { key?: string; question?: string }[] = Array.isArray(
        prevTurn?.ask_user?.clarifications
      )
        ? (prevTurn?.ask_user?.clarifications as {
            key?: string
            question?: string
          }[])
        : []

      let recapRows: { question: string; answer: string }[] = []
      if (action === "answer_question") {
        const fmt = (v: unknown): string =>
          Array.isArray(v) ? v.join(", ") : v == null ? "" : String(v)
        // Only trust the prior clarifications for pairing a KEYED answer when
        // at least one of their keys actually appears in `answer` — otherwise
        // we're looking at the wrong awaiting turn and every row would falsely
        // read "Skipped". (Within a session the answer keys always match the
        // clarification keys, so genuine pairings pass this check.)
        const keyOverlap =
          answerObj != null &&
          priorClarifs.some(
            (c) =>
              c.key != null &&
              Object.prototype.hasOwnProperty.call(answerObj, c.key)
          )
        if (typeof answer === "string") {
          // Single clarification or free-text → one row. "" → "Skipped".
          recapRows = [
            {
              question:
                priorClarifs[0]?.question ||
                prevTurn?.ask_user?.prompt ||
                _t("os_agents.label_your_answer"),
              answer
            }
          ]
        } else if (answerObj && priorClarifs.length > 0 && keyOverlap) {
          // Multi-clarification → pair each question with its answer; a
          // clarification whose key is absent from `answer` was skipped.
          recapRows = priorClarifs.map((c) => ({
            question: c.question || _humanizeSnakeCase(c.key || ""),
            answer: fmt(answerObj[c.key as string])
          }))
        } else if (answerObj) {
          // No reliable pairing → reflect the actual answer with humanized
          // keys (never invents "Skipped" rows from a mismatched prior turn).
          recapRows = Object.entries(answerObj).map(([k, v]) => ({
            question: _humanizeSnakeCase(k),
            answer: fmt(v)
          }))
        }
      }

      if (recapRows.length > 0) {
        stepRows.push({
          id: turnIdx * 1_000_000,
          eventType: "your_answers",
          title: _t("os_agents.label_your_answers"),
          status: "done",
          raw: { answers: recapRows }
        })
      } else {
        // approve / modify / cancel (or an unbuildable answer) → flat summary.
        // Humanize keys so the row reads naturally (`Account Scope: 7-Eleven
        // …`) instead of leaking the planner's internal sentinel keys.
        const summary =
          typeof answer === "string"
            ? answer
            : answer && typeof answer === "object"
              ? Object.entries(answer)
                  .map(
                    ([k, v]) =>
                      `${_humanizeSnakeCase(k)}: ${Array.isArray(v) ? v.join(", ") : String(v)}`
                  )
                  .join(" · ")
              : ""
        stepRows.push({
          id: turnIdx * 1_000_000,
          eventType: "user_decision",
          title: summary
            ? _t("os_agents.label_user_chose_action_with_summary", {
                action: friendlyAction,
                summary
              })
            : _t("os_agents.label_user_chose_action", {
                action: friendlyAction
              }),
          status: "done"
        })
      }
    }
  }
  // Only the last turn's awaiting_user prompt is live; older ones were
  // already answered by a follow-up resume turn.
  const isAwaiting = isLast && turn.status === "awaiting_user"
  // In-progress = page reloaded mid-turn; we'll re-attach to the live
  // SSE stream and re-stream py's events into the same bubble. Skip
  // painting historical step rows — py's stream replays from
  // event_id=0, so the live frames will populate the timeline fresh.
  // Painting them now would stack two copies on top of each other.
  const isInProgress = isLast && turn.status === "in_progress"
  if (!isInProgress) {
    const events = turn.assistant_steps || []
    for (let i = 0; i < events.length; i++) {
      const evt = events[i]
      // plan_step_failed is a back-patch event: live mode walks back
      // through agentSteps and flips the matching plan_step_started /
      // plan_step_fallback_triggered rows for this step_id from `done`
      // (or `running`) to `failed`. NO new chip is added. History
      // historically returned null here so a failed step looked
      // identical to a successful one (green ✓ on the started row).
      // Mirror live's back-patch by mutating stepRows in place.
      if (evt?.event_type === "plan_step_failed") {
        const failedStepId = evt.step_id
        for (let j = stepRows.length - 1; j >= 0; j--) {
          const r = stepRows[j]
          if (
            r.stepId === failedStepId &&
            (r.eventType === "plan_step_started" ||
              r.eventType === "plan_step_fallback_triggered")
          ) {
            stepRows[j] = { ...r, status: "failed" }
          }
        }
        continue
      }
      const row = _eventToStep(evt, turnIdx * 1_000_000 + i + 1, isAwaiting)
      if (row) stepRows.push(row)
    }
  }

  const isFailed = turn.status === "failed"

  // Reconstruct the live-flow askUser block when reloading mid-turn.
  // Two flavors of awaiting_user, both reachable on reload:
  //   1. Clarification prompt — turn.ask_user is populated (planner emitted
  //      an `ask_user` frame). Render the multi-question form.
  //   2. Plan approval — turn.ask_user is null because the plan_proposed
  //      flow doesn't use ask_user; the plan lives inside the trailing
  //      `plan_proposed` step. Pull it out and rebuild the same shape the
  //      live processV2Frame's `case "plan_proposed"` sets, so the FE
  //      renders the Approve/Cancel card identically post-reload.
  let askUserBlock: any = null
  if (isAwaiting) {
    if (turn.ask_user?.clarifications?.length) {
      // Prefer the BE-supplied prompt — in-plan step_confirmation
      // gates ship a meaningful one ("Confirm DXP competitors:"). Fall
      // back to generic phrasing only for legacy planner_clarification
      // turns that never carried a prompt field.
      const replayPrompt =
        (typeof (turn.ask_user as any)?.prompt === "string" &&
          (turn.ask_user as any).prompt) ||
        _t("os_agents.label_please_respond_continue")
      askUserBlock = {
        source: turn.ask_user.source || "execution_ask_user",
        prompt: replayPrompt,
        clarifications: turn.ask_user.clarifications
      }
    } else {
      // Pull the most recent ask_user event from the persisted timeline.
      // Free-form agent asks (PMB v2 / ask_user_tool) ship the question
      // verbatim in `awaiting.prompt` and don't populate a clarifications
      // array. Without this branch the FE rendered an empty bubble after
      // reload — user had to retype the original query to recover.
      const askUserEvent = [...(turn.assistant_steps || [])]
        .reverse()
        .find((e: any) => e?.event_type === "ask_user")
      const replayedAwaitingPrompt =
        (typeof (turn.ask_user as any)?.prompt === "string" &&
          (turn.ask_user as any).prompt) ||
        (typeof askUserEvent?.awaiting?.prompt === "string" &&
          askUserEvent.awaiting.prompt) ||
        (typeof askUserEvent?.prompt === "string" && askUserEvent.prompt) ||
        ""
      if (replayedAwaitingPrompt) {
        // Mirror the live processV2Frame `case "ask_user"` synthesis: one
        // free-text clarification with `question: ""` so AskUserCard
        // renders the prompt as the heading and the input bare beneath
        // (ENG-1254 — duplicate prompt + label). Submit produces the same
        // {action: "answer_question", answer: "<typed>"} shape as the live
        // path.
        askUserBlock = {
          source:
            turn.ask_user?.source ||
            askUserEvent?.awaiting?.source ||
            "execution_ask_user",
          prompt: replayedAwaitingPrompt,
          clarifications: [
            {
              key: "answer",
              question: "",
              answer_type:
                (askUserEvent?.awaiting?.answer_type as string) || "text",
              default: (askUserEvent?.awaiting?.default as string) || ""
            }
          ]
        }
      } else {
        const planProposed = [...(turn.assistant_steps || [])]
          .reverse()
          .find((e: any) => e?.event_type === "plan_proposed")
        if (planProposed?.plan) {
          askUserBlock = {
            source: "plan_approval",
            prompt:
              planProposed.rationale ||
              _t("os_agents.label_review_plan_and_approve"),
            plan: planProposed.plan,
            options: [
              { value: "approve", label: _t("os_agents.cta_approve_and_run") },
              { value: "cancel", label: _t("os_agents.cta_cancel") }
            ]
          }
        }
      }
    }
  }
  // Reconstruct the ordered card list from raw ui_component events in
  // assistant_steps. Each leaf step that produced a card has its own
  // `ui_component` SSE event persisted to agent_invocation_event_stream
  // — we iterate them in order and build the same `cards` array the
  // live handler builds. Falls back to `turn.ui_component` (legacy
  // single-card shape) when no events are found, so older sessions
  // still replay correctly.
  const uiComponentEvents = (turn.assistant_steps || []).filter(
    (e: any) => e?.event_type === "ui_component" && e?.resource_name
  )
  const replayCards: any[] = []
  const seenStepIds = new Set<string>()
  for (const ev of uiComponentEvents) {
    const stepKey = ev?.step_id || ev?.resource_name
    if (seenStepIds.has(stepKey)) continue
    seenStepIds.add(stepKey)
    replayCards.push({
      stepId: ev?.step_id || "",
      resourceName: ev?.resource_name,
      resourceParams: ev?.params || {}
    })
  }
  if (
    replayCards.length === 0 &&
    !!turn.ui_component &&
    !!turn.ui_component.resource_name
  ) {
    replayCards.push({
      stepId: "",
      resourceName: turn.ui_component.resource_name,
      resourceParams: turn.ui_component.params || {}
    })
  }
  // Cache-layer-2 overlay for PptBuilderV2. The agent stamps its
  // `ui_component` SSE event with mode="generate" at planning time and
  // that event stays frozen in agent_invocation_event_stream / assistant_steps.
  // After generate finishes, FE's attach-ppt-session writes the linked
  // ppt_session_id (and mode="resume") into agent_session_chat_history's
  // turn-level `ui_component` column. Cross-device replays must prefer
  // the turn-level params so AgentPptOpusPanel mounts in resume mode
  // instead of regenerating against an empty localStorage. Same-browser
  // localStorage hits still short-circuit before this matters; cross-
  // browser / fresh-session reopens depend on this overlay.
  const turnUi: any = turn.ui_component
  if (
    turnUi?.resource_name === "PptBuilderV2" &&
    turnUi?.params?.ppt_session_id
  ) {
    for (const card of replayCards) {
      if (card.resourceName === "PptBuilderV2") {
        card.resourceParams = { ...card.resourceParams, ...turnUi.params }
      }
    }
  }
  const hasUiComponent = replayCards.length > 0

  // Build content. ui_component path mirrors agent-search-v2's `case
  // "ui_component"` — content_type=ui_component with resourceName +
  // resourceParams; parts[0] carries synth's intro text from
  // final_answer so the bubble shows text-line above, card stack below.
  let content: any
  let content_type: string
  if (hasUiComponent) {
    const firstCard = replayCards[0]
    content = {
      parts: [turn.final_answer || ""],
      resourceName: firstCard.resourceName,
      resourceParams: firstCard.resourceParams,
      cards: replayCards
    }
    content_type = "ui_component"
  } else {
    content = turn.final_answer
      ? { content_type: "text", parts: [turn.final_answer] }
      : ""
    content_type = "text"
  }

  // Pull the synthetic sources_collected event (filtered out of
  // stepRows above) so we can rebuild message.source for the Sources
  // and External-sources buttons after a refresh.
  const sourcesEvt: any = (turn.assistant_steps || []).find(
    (e: any) => e?.event_type === "sources_collected"
  )
  const sourceDocIds: string[] = Array.isArray(sourcesEvt?.source_doc_ids)
    ? sourcesEvt.source_doc_ids
    : []
  const webSearch = sourcesEvt?.web_search_results ?? null
  const initialSource =
    sourceDocIds.length > 0 || webSearch
      ? {
          // Placeholder until the async resolve call below comes back.
          // External-sources renders immediately from web_search_results;
          // Sources drawer fills in once enrichment completes.
          mlRecommendedSearchDocs: [] as any[],
          web_search_results: webSearch
        }
      : []

  // Always check if a live bubble for this invocation_id already
  // exists in the store. The live sendMessage path creates bubbles
  // with id=`${Date.now()+1}` (timestamp) while history-replay uses
  // a synthetic `${invocation_id}_assistant` id — if BOTH paths fire
  // for the same invocation without dedup, the user sees the same
  // assistant content twice (ENG-1295 RCA, 2026-05-20: terminal
  // turn from prior query rebuilt by replay alongside the existing
  // live bubble → "previous response shown again in follow-up
  // query").
  //
  // Previously dedup was gated on `isInProgress` only, on the
  // assumption that terminal turns are fully reconstructable from
  // history alone. That assumption holds when the page was reloaded
  // from scratch — but in the live "user submits a follow-up" path,
  // the previous terminal bubble is still in the store and the
  // replay would create a second copy of it. Apply the dedup
  // unconditionally; the only difference between branches is whether
  // we PRESERVE the live bubble's accumulated content (in_progress)
  // or REPLACE it with the canonical history-derived content
  // (terminal — history has the authoritative version, e.g. after a
  // critic retry that the live stream may have missed).
  const existingBubbleId = turn.invocation_id
    ? _findExistingBubbleForInvocation(sessionId, turn.invocation_id)
    : null
  const messageId = existingBubbleId || `${invId}_assistant`
  if (existingBubbleId && isInProgress) {
    // In-progress reuse: minimal touch — only assert isPending=true
    // and clear any error a prior render might have set. PRESERVE
    // the accumulated agentSteps / content / askUser from the live
    // stream. The re-attach replays from event_id=0 so frames the
    // user already saw aren't lost — they're stored on this bubble.
    setMessages(sessionId, {
      role: "assistant",
      id: messageId,
      isPending: true,
      error: undefined,
      suggestions: (turn.suggestions || []) as SuggestionItem[],
      invocationId: turn.invocation_id || undefined
    })
  } else {
    // Terminal reuse OR fresh bubble. For terminal reuse the live
    // bubble is the SAME message; we overwrite content with the
    // history-derived shape (zustand's setMessages does a shallow
    // top-level merge — content/agentSteps/etc. get replaced wholesale,
    // which is correct here since history is authoritative for a
    // completed turn). For fresh, this is the first-ever write to the
    // synthetic id.
    setMessages(sessionId, {
      role: "assistant",
      id: messageId,
      content,
      content_type,
      agentSteps: stepRows,
      askUser: askUserBlock,
      // In-progress = the page reloaded mid-turn. Leave the bubble in
      // its loading state; the route loader will call sendMessageRef.
      // current.reattach(...) which re-opens the SSE and streams the
      // remaining frames in.
      isPending: isInProgress,
      source: initialSource,
      suggestions: (turn.suggestions || []) as SuggestionItem[],
      ...(isFailed && { error: _genericErrorMessage() }),
      invocationId: turn.invocation_id || undefined
    })
  }

  // Async enrichment — same /v2/.../sources/resolve endpoint the live
  // done-handler uses. Fire-and-forget; failures leave the placeholder
  // in place (External-sources still renders, Sources drawer stays
  // empty). Skip when caller didn't pass tokenInfo (e.g., legacy
  // callers that don't need RFP source enrichment).
  if (
    sourceDocIds.length > 0 &&
    tokenInfo?.tenant_id &&
    tokenInfo?.user_id &&
    tokenInfo?.token
  ) {
    void (async () => {
      try {
        const v2Base = `${URLS.V1.replace("/v1", "/v2")}`
        const res = await fetch(
          `${v2Base}/tenant/${tokenInfo.tenant_id}/users/${tokenInfo.user_id}/copilot/sources/resolve`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${tokenInfo.token}`
            },
            body: JSON.stringify({
              source_ids: sourceDocIds,
              web_search_results: webSearch
            })
          }
        )
        if (!res.ok) return
        const json = await res.json()
        if (json?.success && json?.data) {
          setMessages(sessionId, {
            id: messageId,
            source: {
              mlRecommendedSearchDocs: json.data.mlRecommendedSearchDocs || [],
              web_search_results: json.data.web_search_results
            }
          })
        }
      } catch (err) {
        // best-effort enrichment — surface for ENG-1167/1293 visibility.
        // eslint-disable-next-line no-console
        console.error(
          "[v2-debug] history-replay sources enrichment failed",
          err
        )
      }
    })()
  }
}
